<template>
  <div class="mosta-select-wrapper">
    <div
      ref="triggerRef"
      class="mosta-select"
      :class="{
        'mosta-select--open': isOpen,
        'mosta-select--disabled': disable,
        'mosta-select--focused': isFocused,
      }"
      :tabindex="disable ? -1 : 0"
      @click="toggle"
      @keydown="onTriggerKeydown"
      @focus="onFocus"
      @blur="onBlur"
    >
      <span v-if="displayText" class="mosta-select__value">{{ displayText }}</span>
      <span v-else class="mosta-select__placeholder">{{ label }}</span>
      <svg
        class="mosta-select__chevron"
        :class="{ 'mosta-select__chevron--open': isOpen }"
        width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M4 6L8 10L12 6" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>

    <Teleport to="body">
      <Transition name="mosta-select-dropdown">
        <div
          v-if="isOpen"
          ref="floatingRef"
          class="mosta-select__dropdown"
          :style="floatingStyles"
          @mousedown.prevent
        >
          <div class="mosta-select__options" ref="optionsRef">
            <div
              v-if="!options || !options.length"
              class="mosta-select__no-option"
            >
              <slot name="no-option">
                <span class="mosta-select__no-option-text">{{ $t('no_data_found') }}</span>
              </slot>
            </div>
            <div
              v-for="(opt, idx) in options"
              :key="getOptionKey(opt, idx)"
              class="mosta-select__item"
              :class="{
                'mosta-select__item--active': isSelected(opt),
                'mosta-select__item--highlighted': highlightedIndex === idx,
                'mosta-select__item--divider': isDivider(opt),
              }"
              :ref="el => setItemRef(el, idx)"
              @click.stop="onOptionClick(opt)"
              @mouseenter="onOptionMouseEnter(idx, opt)"
            >
              <div v-if="isDivider(opt)" class="mosta-select__divider"></div>
              <template v-else>{{ getOptionLabel(opt) }}</template>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Emit } from "vue-facing-decorator";
import { computePosition, offset, flip, shift, size, autoUpdate } from "@floating-ui/dom";
import { nextTick } from "vue";

@Component({ name: "MostaSelect" })
export default class MostaSelect extends Vue {
  @Prop({ default: null }) modelValue: any;
  @Prop({ default: () => [] }) options!: any[];
  @Prop({ default: "label" }) optionLabel!: string | ((o: any) => string);
  @Prop({ default: null }) optionValue!: string | ((o: any) => any) | null;
  @Prop({ default: "" }) label!: string;
  @Prop({ default: false }) disable!: boolean;

  @Emit("update:modelValue")
  emitUpdate(val: any) { return val; }

  isOpen = false;
  isFocused = false;
  highlightedIndex = -1;
  private suppressNextFocus = false;

  private itemRefs: (HTMLElement | null)[] = [];
  private cleanup: (() => void) | null = null;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  private escapeHandler: ((e: KeyboardEvent) => void) | null = null;
  private wheelHandler: ((e: WheelEvent) => void) | null = null;

  floatingStyles: Record<string, string> = { position: "absolute", left: "0px", top: "0px" };

  setItemRef(el: any, idx: number) {
    this.itemRefs[idx] = el as HTMLElement;
  }

  isDivider(opt: any): boolean {
    return !!opt && typeof opt === "object" && !!opt.divider;
  }

  getOptionKey(opt: any, idx: number): any {
    if (this.isDivider(opt)) return `divider-${idx}`;
    return this.getOptionValue(opt);
  }

  private firstSelectableIndex(): number {
    if (!this.options?.length) return -1;
    return this.options.findIndex((opt) => !this.isDivider(opt));
  }

  private lastSelectableIndex(): number {
    if (!this.options?.length) return -1;
    for (let i = this.options.length - 1; i >= 0; i--) {
      if (!this.isDivider(this.options[i])) return i;
    }
    return -1;
  }

  private nextSelectableIndex(from: number, dir: 1 | -1): number {
    if (!this.options?.length) return -1;
    let i = from;
    while (i >= 0 && i < this.options.length) {
      if (!this.isDivider(this.options[i])) return i;
      i += dir;
    }
    return -1;
  }

  get displayText(): string {
    if (this.modelValue == null) return "";
    const opt = this.options?.find(o => this.valuesEqual(this.getOptionValue(o), this.modelValue));
    return opt != null ? this.getOptionLabel(opt) : "";
  }

  getOptionLabel(opt: any): string {
    if (opt == null) return "";
    if (typeof this.optionLabel === "function") return this.optionLabel(opt);
    if (typeof opt === "object") return opt[this.optionLabel] ?? "";
    return String(opt);
  }

  getOptionValue(opt: any): any {
    if (opt == null) return null;
    if (this.optionValue == null) return opt;
    if (typeof this.optionValue === "function") return this.optionValue(opt);
    if (typeof opt === "object") return opt[this.optionValue];
    return opt;
  }

  valuesEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (typeof a === "object" && typeof b === "object") {
      return JSON.stringify(a) === JSON.stringify(b);
    }
    return false;
  }

  isSelected(opt: any): boolean {
    return this.valuesEqual(this.getOptionValue(opt), this.modelValue);
  }

  get selectedIndex(): number {
    if (this.modelValue == null || !this.options?.length) return -1;
    return this.options.findIndex(
      (o) => !this.isDivider(o) && this.valuesEqual(this.getOptionValue(o), this.modelValue)
    );
  }

  toggle() {
    if (this.disable) return;
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.disable || this.isOpen) return;
    this.isOpen = true;
    this.highlightedIndex = this.selectedIndex >= 0 ? this.selectedIndex : this.firstSelectableIndex();
    this.itemRefs = [];

    // floating-ui считает позицию асинхронно, поэтому координаты по триггеру ставим сразу:
    // телепортированный в body список обязан быть вне потока с первого кадра.
    const trigger = this.$refs.triggerRef as HTMLElement | undefined;
    const rect = trigger?.getBoundingClientRect();
    if (rect) {
      this.floatingStyles = {
        position: "absolute",
        left: `${rect.left + window.scrollX}px`,
        top: `${rect.bottom + 4 + window.scrollY}px`,
      };
    }

    nextTick(() => {
      this.setupFloating();
      this.scrollToHighlighted();
    });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    // При закрытии всегда снимаем обводку, даже если фокус формально остался.
    this.isFocused = false;
    this.destroyFloating();
  }

  selectOption(opt: any) {
    if (this.isDivider(opt)) return;
    this.emitUpdate(this.getOptionValue(opt));
    this.close();
    this.suppressNextFocus = true;
    (this.$refs.triggerRef as HTMLElement)?.focus();
  }

  onOptionClick(opt: any) {
    this.selectOption(opt);
  }

  onOptionMouseEnter(idx: number, opt: any) {
    if (this.isDivider(opt)) return;
    this.highlightedIndex = idx;
  }

  onFocus() {
    if (this.suppressNextFocus) {
      this.suppressNextFocus = false;
      this.isFocused = false;
      return;
    }
    this.isFocused = true;
  }

  onBlur(e: FocusEvent) {
    const related = e.relatedTarget as HTMLElement | null;
    const floating = this.$refs.floatingRef as HTMLElement | undefined;
    if (floating && related && floating.contains(related)) return;
    this.isFocused = false;
    this.close();
  }

  onTriggerKeydown(e: KeyboardEvent) {
    if (this.disable) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (this.isOpen) {
          this.moveHighlight(1);
        } else {
          this.cycleValue(1);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (this.isOpen) {
          this.moveHighlight(-1);
        } else {
          this.cycleValue(-1);
        }
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (this.isOpen) {
          if (
            this.highlightedIndex >= 0 &&
            this.highlightedIndex < (this.options?.length ?? 0) &&
            !this.isDivider(this.options[this.highlightedIndex])
          ) {
            this.selectOption(this.options[this.highlightedIndex]);
          }
        } else {
          this.open();
        }
        break;
      case "Escape":
        if (this.isOpen) {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        }
        break;
      case "Tab":
        this.close();
        break;
      case "Home":
        if (this.isOpen && this.options?.length) {
          e.preventDefault();
          this.highlightedIndex = this.firstSelectableIndex();
          this.scrollToHighlighted();
        }
        break;
      case "End":
        if (this.isOpen && this.options?.length) {
          e.preventDefault();
          this.highlightedIndex = this.lastSelectableIndex();
          this.scrollToHighlighted();
        }
        break;
      case "PageDown":
        if (this.isOpen && this.options?.length) {
          e.preventDefault();
          this.moveHighlight(10);
        }
        break;
      case "PageUp":
        if (this.isOpen && this.options?.length) {
          e.preventDefault();
          this.moveHighlight(-10);
        }
        break;
    }
  }

  cycleValue(dir: number) {
    if (!this.options?.length) return;
    const available = this.options.filter((opt) => !this.isDivider(opt));
    if (!available.length) return;

    const cur = available.findIndex((opt) =>
      this.valuesEqual(this.getOptionValue(opt), this.modelValue)
    );
    let next = cur + dir;
    if (cur < 0) next = dir > 0 ? 0 : available.length - 1;
    if (next < 0) next = available.length - 1;
    if (next >= available.length) next = 0;
    this.emitUpdate(this.getOptionValue(available[next]));
  }

  moveHighlight(dir: number) {
    if (!this.options?.length) return;
    const normalizedDir: 1 | -1 = dir > 0 ? 1 : -1;
    let next = this.highlightedIndex;
    if (next < 0) {
      next = normalizedDir > 0 ? this.firstSelectableIndex() : this.lastSelectableIndex();
    } else {
      next = this.nextSelectableIndex(next + normalizedDir, normalizedDir);
    }
    if (next < 0) return;
    this.highlightedIndex = next;
    this.scrollToHighlighted();
  }

  // Скроллим только контейнер списка. scrollIntoView тут не подходит: он двигает все
  // скроллящиеся родители вплоть до окна, а список телепортирован в body.
  scrollToHighlighted() {
    nextTick(() => {
      const el = this.itemRefs[this.highlightedIndex];
      const box = this.$refs.optionsRef as HTMLElement | undefined;
      if (!el || !box) return;

      const top = el.offsetTop;
      const bottom = top + el.offsetHeight;
      if (top < box.scrollTop) {
        box.scrollTop = top;
      } else if (bottom > box.scrollTop + box.clientHeight) {
        box.scrollTop = bottom - box.clientHeight;
      }
    });
  }

  private setupFloating() {
    const trigger = this.$refs.triggerRef as HTMLElement;
    const floating = this.$refs.floatingRef as HTMLElement;
    if (!trigger || !floating) return;

    this.cleanup = autoUpdate(trigger, floating, () => {
      computePosition(trigger, floating, {
        placement: "bottom-start",
        middleware: [
          offset(4),
          flip(),
          shift(),
          size({
            apply({ availableHeight, rects, elements }) {
              Object.assign(elements.floating.style, {
                width: `${rects.reference.width}px`,
                maxHeight: `${Math.min(availableHeight, 300)}px`,
              });
            },
          }),
        ],
      }).then(({ x, y }) => {
        this.floatingStyles = {
          position: "absolute",
          left: `${x}px`,
          top: `${y}px`,
        };
      });
    });

    this.outsideClickHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!trigger.contains(target) && !floating.contains(target)) {
        this.close();
      }
    };
    document.addEventListener("mousedown", this.outsideClickHandler);

    this.escapeHandler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      this.close();
    };
    document.addEventListener("keydown", this.escapeHandler, true);

    // Список на body: колесо иначе уходит на страницу справа (таблица/графики).
    this.wheelHandler = (e: WheelEvent) => this.containDropdownWheel(e);
    floating.addEventListener("wheel", this.wheelHandler, { passive: false, capture: true });
  }

  private containDropdownWheel(e: WheelEvent) {
    e.preventDefault();
    e.stopPropagation();
    const el = this.$refs.optionsRef as HTMLElement | undefined;
    if (el) el.scrollTop += e.deltaY;
  }

  private destroyFloating() {
    const floating = this.$refs.floatingRef as HTMLElement | undefined;
    if (this.wheelHandler && floating) {
      floating.removeEventListener("wheel", this.wheelHandler, true);
    }
    this.wheelHandler = null;
    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }
    if (this.outsideClickHandler) {
      document.removeEventListener("mousedown", this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (this.escapeHandler) {
      document.removeEventListener("keydown", this.escapeHandler, true);
      this.escapeHandler = null;
    }
  }

  beforeUnmount() {
    this.destroyFloating();
  }
}
</script>

<style lang="scss" scoped>
.mosta-select-wrapper {
  margin: 4px 0;
}

.mosta-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--color-border, #D2D2D2);
  background: var(--Background-White, #FFF);
  cursor: pointer;
  outline: none;
  box-sizing: border-box;
  gap: 8px;
  user-select: none;

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  &--focused,
  &--open {
    border-color: var(--color-accent, #3F884F);
  }

  &:not(.mosta-select--disabled):hover {
    border-color: var(--color-accent, #3F884F);
  }

  &__value {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--Text-color-text_main, #4D4D4D);
    font-family: Roboto;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
  }

  &__placeholder {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #999;
    font-family: Roboto;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
  }

  &__chevron {
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  &__chevron--open {
    transform: rotate(180deg);
  }
}
</style>

<style lang="scss">
.mosta-select__dropdown {
  z-index: 6000;
  border-radius: 8px;
  border: 1px solid var(--color-border, #D2D2D2);
  background: var(--Background-White, #FFF);
  padding: 4px;
  box-shadow: none;
  box-sizing: border-box;
}

.mosta-select__options {
  overflow-y: auto;
  overscroll-behavior: contain;
  max-height: inherit;
}

.mosta-select__item {
  display: flex;
  align-items: flex-start;
  height: auto;
  min-height: 28px;
  padding: 4px 12px 4px 8px;
  border-radius: 4px;
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: var(--Text-color-text_main, #4D4D4D);
  cursor: pointer;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;

  &--highlighted {
    background: var(--color-divider, #F2F2F2);
  }

  &--active {
    color: var(--color-accent, #3F884F);
  }

  &--active.mosta-select__item--highlighted {
    background: #F3F8F4;
  }

  &--divider {
    height: auto;
    min-height: 0;
    padding: 1px 0;
    cursor: default;
  }
}

.mosta-select__divider {
  width: 100%;
  height: 1px;
  margin: 1px 0;
  background: #F2F2F2;
}

.mosta-select__no-option {
  padding: 8px 12px;
  color: #999;
  font-family: Roboto;
  font-size: 14px;
  line-height: 18px;
}

.mosta-select-dropdown-enter-active,
.mosta-select-dropdown-leave-active {
  transition: opacity 0.15s ease;
}

.mosta-select-dropdown-enter-from,
.mosta-select-dropdown-leave-to {
  opacity: 0;
}
</style>
